import { Inject, Injectable, NgZone } from '@angular/core';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
    afterLoginSuccess,
    afterLogoutSuccess,
    determineNativePlatformSuccess,
    determinePlatform,
    determinePlatformError,
    determinePlatformSuccess,
    openOsNotificationSettings,
    openOsNotificationSettingsError,
    pushNotificationActionPerformed,
    pushNotificationAddListeners,
    pushNotificationCheckPermission,
    pushNotificationError,
    pushNotificationListenersAdded,
    pushNotificationListenersRemoved,
    pushNotificationReceived,
    pushNotificationTokenReceived,
    pushNotificationTokenStored,
    sendToken,
    sendTokenFailure,
    sendTokenSuccess,
    updateNotificationsChannelsError,
    updateNotificationsChannelsRequest,
    updateNotificationsChannelsSuccess
} from './permissions.actions';
import {
    catchError,
    concatMap,
    delay,
    filter,
    map,
    retryWhen,
    scan,
    switchMap,
    take,
    tap,
    timeout,
    withLatestFrom
} from 'rxjs/operators';
import { defer, EMPTY, from, fromEventPattern, of } from 'rxjs';
import { PushNotifications } from '@capacitor/push-notifications';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Preferences } from '@capacitor/preferences';
import { DeviceNotificationService } from '../firebase/device-notification.service';
import { selectIsNativeApp, selectListenersAdded, selectPushNotificationToken } from './permissions.selectors';
import Env from '@ridango/core/env';
import { NotificationChannel, NotificationSetting, NotificationSettingsService, Platform } from '@ridango/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationFacade } from '../application/+state/application.facade';

@Injectable({
    providedIn: 'root'
})
export class PermissionsEffects implements OnInitEffects {
    constructor(
        @Inject('ENV') public env: Env,
        private actions$: Actions,
        private store$: Store,
        private router: Router,
        private translate: TranslateService,
        private ngZone: NgZone,
        private applicationFacade: ApplicationFacade,
        private deviceNotificationService: DeviceNotificationService,
        private notificationSettingsService: NotificationSettingsService
    ) {}

    appStateChange$ = createEffect(() =>
        fromEventPattern(
            (handler) => {
                return CapacitorApp.addListener('appStateChange', handler);
            },
            (handler, listener) => listener.remove()
        ).pipe(
            filter((state: { isActive: boolean }) => state.isActive),
            map(() => pushNotificationCheckPermission())
        )
    );

    handlePushNotificationPermission$ = createEffect(() =>
        this.actions$.pipe(
            ofType(pushNotificationCheckPermission),
            switchMap(() =>
                from(PushNotifications.checkPermissions()).pipe(
                    switchMap((status) => {
                        if (status.receive === 'granted') {
                            return of(pushNotificationAddListeners());
                        } else if (status.receive === 'prompt') {
                            return from(PushNotifications.requestPermissions()).pipe(
                                switchMap((result) =>
                                    result.receive === 'granted'
                                        ? of(pushNotificationAddListeners())
                                        : of(pushNotificationError({ error: 'Permission denied' }))
                                ),
                                catchError((error) => of(pushNotificationError({ error })))
                            );
                        } else {
                            return this.store$.select(selectListenersAdded).pipe(
                                take(1),
                                switchMap((listenersAdded) =>
                                    !listenersAdded
                                        ? EMPTY
                                        : from(PushNotifications.unregister()).pipe(
                                              concatMap(() =>
                                                  from(PushNotifications.removeAllListeners()).pipe(
                                                      switchMap(() => of(pushNotificationListenersRemoved()))
                                                  )
                                              ),
                                              catchError((error) => of(pushNotificationError({ error })))
                                          )
                                )
                            );
                        }
                    }),
                    catchError((error) => of(pushNotificationError({ error })))
                )
            )
        )
    );

    onOpenOsNotificationSettings$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(openOsNotificationSettings),
                switchMap(() =>
                    from(
                        Capacitor.getPlatform() === Platform.ANDROID
                            ? NativeSettings.openAndroid({ option: AndroidSettings.AppNotification })
                            : NativeSettings.openIOS({ option: IOSSettings.App })
                    ).pipe(
                        catchError((error) => {
                            return of(openOsNotificationSettingsError({ error }));
                        })
                    )
                )
            ),
        { dispatch: false }
    );

    onDeterminePlatform$ = createEffect(() =>
        this.actions$.pipe(
            ofType(determinePlatform),
            switchMap(() => {
                try {
                    const platform: Platform = Capacitor.getPlatform() as Platform;
                    const isNativeApp = Capacitor.isNativePlatform;
                    if (isNativeApp) {
                        this.store$.dispatch(determineNativePlatformSuccess());
                        this.store$.dispatch(pushNotificationCheckPermission());
                    }
                    return of(determinePlatformSuccess({ platform }));
                } catch (error) {
                    console.error('Error determining platform:', error);
                    return of(determinePlatformError(error));
                }
            })
        )
    );

    addListeners$ = createEffect(() =>
        this.actions$.pipe(
            ofType(pushNotificationAddListeners),
            withLatestFrom(this.store$.select(selectListenersAdded), this.store$.select(selectIsNativeApp)),
            filter(([action, listenersAdded, isNativeApp]) => !listenersAdded && isNativeApp),
            switchMap(([action, listenersAdded, isNativeApp]) => {
                PushNotifications.addListener('registration', async (token) => {
                    this.store$.dispatch(pushNotificationTokenReceived({ token: token.value }));
                });

                PushNotifications.addListener('registrationError', (err) => {
                    console.error('Push notification registration error:', err.error);
                    this.store$.dispatch(pushNotificationError({ error: err.error }));
                });

                PushNotifications.addListener('pushNotificationReceived', (notification) => {
                    const { aps } = notification.data;
                    const { badge } = aps;
                    // Android doesn't support auto update badge on push receive
                    if (Capacitor.getPlatform() === 'android') {
                        if (typeof badge === 'number') {
                            this.deviceNotificationService.setBadgeCount(badge);
                        }
                    }
                    this.store$.dispatch(pushNotificationReceived({ notification }));
                });

                PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
                    this.store$.dispatch(pushNotificationActionPerformed({ notification: notification.notification }));
                });

                return from(PushNotifications.register()).pipe(
                    map(() => pushNotificationListenersAdded()),
                    catchError((error) => {
                        console.error('Push notification registration failed:', error);
                        return of(pushNotificationError({ error }));
                    })
                );
            })
        )
    );

    storePushNotificationToken$ = createEffect(() =>
        this.actions$.pipe(
            ofType(pushNotificationTokenReceived),
            switchMap(({ token }) =>
                from(Preferences.get({ key: 'firebase-token' })).pipe(
                    switchMap((storedToken) => {
                        if (storedToken.value !== token) {
                            return from(Preferences.set({ key: 'firebase-token', value: token })).pipe(
                                map(() => pushNotificationTokenStored({ token })),
                                catchError((error) => {
                                    console.error('Error storing push notification token:', error);
                                    return of(pushNotificationError({ error: 'Failed to store push token' }));
                                })
                            );
                        } else {
                            return of(pushNotificationTokenStored({ token }));
                        }
                    }),
                    catchError((error) => {
                        console.error('Error retrieving push token from storage:', error);
                        return of(pushNotificationError({ error: 'Failed to retrieve push token' }));
                    })
                )
            )
        )
    );

    /**
     * After login/logout the authentication bearer token changes, it carries customer_id info in JWT
     * Upon token checkin, depending on the bearer token, the FCM token is saved as anon or user token
     * Only one FCM token must exist in the database either for anonymous or authenticated user
     * If new token is generated, the old token might remain in the database until BE deletes it on delivery fail
     * Delete old token + Update token on permission changes (anon or user token)
     * Delete old token + Update token on creation (anon token or user token)
     * Delete old token + Update token on login (becomes user token)
     * Delete old token + Update token on logout (becomes anon token)
     */
    savePushNotificationTokenInDb$ = createEffect(() =>
        this.actions$.pipe(
            ofType(pushNotificationTokenStored, afterLoginSuccess, afterLogoutSuccess),
            withLatestFrom(this.store$.select(selectPushNotificationToken)),
            filter(([action, token]) => !!token),
            switchMap(([action, token]) => {
                return this.deviceNotificationService.removeToken(token).pipe(
                    map(() => sendToken()),
                    catchError((error) => {
                        return of(sendTokenFailure({ error }));
                    })
                );
            })
        )
    );

    onSendToken$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sendToken),
            switchMap(() =>
                this.store$.select(selectPushNotificationToken).pipe(
                    take(1),
                    switchMap((token) =>
                        this.deviceNotificationService.sendToken(token).pipe(
                            retryWhen((errors) =>
                                errors.pipe(
                                    scan((count, err) => {
                                        if (count >= 3) {
                                            throw err;
                                        }
                                        return count + 1;
                                    }, 0),
                                    delay(5000)
                                )
                            ),
                            map(() => sendTokenSuccess({ token })),
                            catchError((error) => of(sendTokenFailure({ error })))
                        )
                    )
                )
            )
        )
    );

    onPushNotificationReceived = createEffect(
        () =>
            this.actions$.pipe(
                ofType(pushNotificationReceived),
                switchMap(({ notification }) => {
                    return of();
                })
            ),
        { dispatch: false }
    );

    appIsReady$ = defer(() =>
        this.applicationFacade.ready$.pipe(
            filter(Boolean),
            take(1),
            timeout(30000),
            retryWhen((errors) => errors.pipe(delay(500)))
        )
    );

    onPushNotificationActionPerformed = createEffect(
        () =>
            this.actions$.pipe(
                ofType(pushNotificationActionPerformed),
                concatMap(({ notification }) =>
                    this.appIsReady$.pipe(
                        tap(() =>
                            this.ngZone.run(() => {
                                const { redirectUrl, docNum, messageId } = notification.data;

                                // Open external link if URL is provided and message display is disabled
                                if (redirectUrl?.length && !this.env.HEADER?.SHOW_MESSAGES) {
                                    this.deviceNotificationService.openExternalLink(redirectUrl);
                                }

                                // Open ticket view if docNum exists
                                else if (docNum) {
                                    this.deviceNotificationService.openTicketDetailView(docNum);
                                }

                                // Navigate to messages if messageId exists and message display is enabled
                                else if (messageId && this.env.HEADER?.SHOW_MESSAGES) {
                                    const lang = this.translate.currentLang;
                                    this.applicationFacade.setMobileMenuOpen(false);
                                    this.router.navigate([`/${lang}/account/messages/${messageId}`], {
                                        queryParams: { messageId, push: true },
                                        skipLocationChange: true
                                    });
                                }
                            })
                        )
                    )
                )
            ),
        { dispatch: false }
    );

    /**
     * Equally important to sending FCM token to BE is to update the notification channels
     * Without having the channels updated, the user will not receive notifications from Notification Portal
     * The channels are updated only when the token is updated, keeping channels in sync
     * for anonymous user and registered user
     */
    loadChannelsOnTokenSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sendTokenSuccess),
            switchMap(() =>
                this.notificationSettingsService.getNotificationSettings().pipe(
                    map(({ channels: fetchedChannels }: NotificationSetting) => {
                        const updateChannels = (
                            channels: NotificationChannel,
                            validChannels: string[]
                        ): NotificationChannel => {
                            return validChannels.reduce(
                                (acc, channel) => {
                                    acc[channel] = true;
                                    return acc;
                                },
                                { ...channels }
                            );
                        };
                        const mergedChannels = updateChannels(fetchedChannels, this.env.NOTIFICATION_CHANNELS);
                        return updateNotificationsChannelsRequest({ channels: mergedChannels });
                    }),
                    catchError((error) => of(updateNotificationsChannelsError({ error })))
                )
            ),
            catchError((error) => of(updateNotificationsChannelsError({ error })))
        )
    );

    updateChannels$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateNotificationsChannelsRequest),
            switchMap((action) =>
                this.notificationSettingsService.updateNotificationSettings({ channels: action.channels }).pipe(
                    retryWhen((errors) =>
                        errors.pipe(
                            scan((acc, err) => {
                                if (acc >= 2) {
                                    throw err;
                                }
                                return acc + 1;
                            }, 0),
                            delay(5000)
                        )
                    ),
                    map((response) => updateNotificationsChannelsSuccess({ channels: response.channels })),
                    catchError((error) => of(updateNotificationsChannelsError({ error })))
                )
            )
        )
    );

    ngrxOnInitEffects(): Action {
        return determinePlatform();
    }
}
